/**
 * CopybookGraph Component
 *
 * Interactive copybook usage visualization showing:
 * - Which programs use which copybooks (COPY statements)
 * - Which copybooks include other copybooks (nested includes)
 * - Shared copybooks highlighted (used by 3+ programs)
 * - Orphan detection (copybooks never used)
 *
 * Features:
 * - Click copybook → highlight all programs using it
 * - Click program → highlight all copybooks it uses
 * - Usage count badges on shared copybooks
 * - Export to PNG/SVG
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react'
import {
  Search, RotateCcw, Maximize2, X, ChevronRight, ChevronDown,
  Loader2, AlertCircle, Eye, EyeOff, Layers, Image, FileText, Info
} from 'lucide-react'
import { toPng, toSvg } from 'html-to-image'
import { nodeTypes } from './GraphNodes'
import { parseCopybookUsage } from '../../utils/copybookParser'
import { applyLayout } from '../../utils/graphLayouts'
import './DependencyGraph.css'

// Node type configuration
const NODE_TYPE_CONFIG = {
  program: { color: '#4a90d9', label: 'Programs' },
  copybook: { color: '#6b8e23', label: 'Copybooks' },
  shared: { color: '#e0af68', label: 'Shared Copybooks' },
}

// Layout options
const LAYOUT_OPTIONS = [
  { value: 'hierarchical', label: 'Hierarchical (Top-Down)' },
  { value: 'hierarchical-lr', label: 'Hierarchical (Left-Right)' },
  { value: 'force', label: 'Force-Directed' },
  { value: 'radial', label: 'Radial' },
]

/**
 * Legend Component
 */
function CopybookLegend({ nodeTypes, visibleTypes, onToggleType }) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div className={`graph-legend ${isCollapsed ? 'collapsed' : ''}`}>
      <button className="legend-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <span>Legend</span>
      </button>

      {!isCollapsed && (
        <div className="legend-items">
          {Object.entries(NODE_TYPE_CONFIG).map(([type, config]) => {
            const count = nodeTypes[type] || 0
            if (count === 0) return null

            const isVisible = visibleTypes.has(type)

            return (
              <button
                key={type}
                className={`legend-item ${isVisible ? 'visible' : 'hidden'}`}
                onClick={() => onToggleType(type)}
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: isVisible ? config.color : '#444' }}
                />
                <span className="legend-label">{config.label}</span>
                <span className="legend-count">{count}</span>
                {isVisible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Info Panel Component
 */
function InfoPanel() {
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div className={`graph-info-panel ${isCollapsed ? 'collapsed' : ''}`}>
      <button className="info-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
        <Info size={14} />
        {!isCollapsed && <span>About</span>}
      </button>

      {!isCollapsed && (
        <div className="info-content">
          <p><strong>Click interactions:</strong></p>
          <ul>
            <li>Click copybook → see all programs using it</li>
            <li>Click program → see all copybooks it uses</li>
            <li>Click background → clear selection</li>
          </ul>
          <p><strong>Color coding:</strong></p>
          <ul>
            <li><span style={{ color: '#4a90d9' }}>●</span> Programs (.cbl)</li>
            <li><span style={{ color: '#6b8e23' }}>●</span> Copybooks (.cpy)</li>
            <li><span style={{ color: '#e0af68' }}>●</span> Shared (3+ users)</li>
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Main Copybook Graph Component
 */
function CopybookGraph({
  copybookData,
  loading = false,
  error = null,
}) {
  const { fitView, setCenter } = useReactFlow()

  // Graph state
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // UI state
  const [selectedNodeId, setSelectedNodeId] = useState(null)
  const [highlightedNodeIds, setHighlightedNodeIds] = useState(new Set())
  const [highlightedEdgeIds, setHighlightedEdgeIds] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleTypes, setVisibleTypes] = useState(new Set(Object.keys(NODE_TYPE_CONFIG)))
  const [layout, setLayout] = useState('hierarchical')
  const [isLayouting, setIsLayouting] = useState(false)

  // Parse and set graph data when it changes
  useEffect(() => {
    if (!copybookData) return

    const parsedData = parseCopybookUsage(copybookData)

    if (parsedData.nodes.length > 0) {
      runLayout(parsedData.nodes, parsedData.edges, layout)
    }
  }, [copybookData])

  // Apply layout
  const runLayout = useCallback(async (nodesToLayout, edgesToLayout, layoutType) => {
    setIsLayouting(true)
    try {
      const layoutedNodes = await applyLayout(layoutType, nodesToLayout, edgesToLayout, {
        width: 1200,
        height: 800,
      })
      setNodes(layoutedNodes)
      setEdges(edgesToLayout)

      // Fit view after layout
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 300 })
      }, 50)
    } catch (err) {
      console.error('Layout error:', err)
      setNodes(nodesToLayout)
      setEdges(edgesToLayout)
    } finally {
      setIsLayouting(false)
    }
  }, [setNodes, setEdges, fitView])

  // Re-layout when layout type changes
  useEffect(() => {
    if (nodes.length > 0) {
      runLayout(nodes, edges, layout)
    }
  }, [layout])

  // Filter nodes based on search and visibility
  const filteredNodes = useMemo(() => {
    return nodes.map(node => {
      const matchesSearch = !searchTerm ||
        node.data.label?.toLowerCase().includes(searchTerm.toLowerCase())

      const isTypeVisible = visibleTypes.has(node.data.nodeType)
      const isHighlighted = highlightedNodeIds.has(node.id)
      const hasHighlights = highlightedNodeIds.size > 0

      return {
        ...node,
        hidden: !matchesSearch || !isTypeVisible,
        className: `${node.className || ''} ${hasHighlights && !isHighlighted ? 'dimmed' : ''} ${isHighlighted ? 'highlighted' : ''}`,
        data: {
          ...node.data,
          highlighted: isHighlighted,
        },
      }
    })
  }, [nodes, searchTerm, visibleTypes, highlightedNodeIds])

  // Filter edges based on visible nodes and highlights
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.filter(n => !n.hidden).map(n => n.id))
    const hasHighlights = highlightedEdgeIds.size > 0

    return edges
      .filter(edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
      .map(edge => ({
        ...edge,
        className: hasHighlights && !highlightedEdgeIds.has(edge.id) ? 'dimmed' : '',
        animated: highlightedEdgeIds.has(edge.id),
      }))
  }, [edges, filteredNodes, highlightedEdgeIds])

  // Calculate node type counts
  const nodeTypeCounts = useMemo(() => {
    const counts = {}
    nodes.forEach(node => {
      const type = node.data.nodeType || 'copybook'
      counts[type] = (counts[type] || 0) + 1
    })
    return counts
  }, [nodes])

  // Handle node click - highlight connections
  const onNodeClick = useCallback((event, node) => {
    if (selectedNodeId === node.id) {
      // Deselect if clicking the same node
      setSelectedNodeId(null)
      setHighlightedNodeIds(new Set())
      setHighlightedEdgeIds(new Set())
      return
    }

    setSelectedNodeId(node.id)

    // Find all connected nodes and edges
    const connectedNodeIds = new Set([node.id])
    const connectedEdgeIds = new Set()

    edges.forEach(edge => {
      if (edge.source === node.id || edge.target === node.id) {
        connectedNodeIds.add(edge.source)
        connectedNodeIds.add(edge.target)
        connectedEdgeIds.add(edge.id)
      }
    })

    setHighlightedNodeIds(connectedNodeIds)
    setHighlightedEdgeIds(connectedEdgeIds)
  }, [edges, selectedNodeId])

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null)
    setHighlightedNodeIds(new Set())
    setHighlightedEdgeIds(new Set())
  }, [])

  // Toggle node type visibility
  const toggleTypeVisibility = useCallback((type) => {
    setVisibleTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }, [])

  // Reset view
  const resetView = useCallback(() => {
    setSearchTerm('')
    setVisibleTypes(new Set(Object.keys(NODE_TYPE_CONFIG)))
    setSelectedNodeId(null)
    setHighlightedNodeIds(new Set())
    setHighlightedEdgeIds(new Set())
    fitView({ padding: 0.2, duration: 300 })
  }, [fitView])

  // Refresh layout
  const refreshLayout = useCallback(() => {
    if (nodes.length > 0) {
      runLayout(nodes, edges, layout)
    }
  }, [nodes, edges, layout, runLayout])

  // Helper to download image
  const downloadImage = (dataUrl, filename) => {
    const link = document.createElement('a')
    link.download = filename
    link.href = dataUrl
    link.click()
  }

  // Export to PNG
  const exportToPNG = useCallback(() => {
    const graphElement = document.querySelector('.react-flow')
    if (!graphElement) return

    toPng(graphElement, {
      backgroundColor: '#0d1117',
      width: graphElement.offsetWidth,
      height: graphElement.offsetHeight,
    })
      .then((dataUrl) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
        downloadImage(dataUrl, `copybook-graph-${timestamp}.png`)
      })
      .catch((err) => {
        console.error('Failed to export PNG:', err)
      })
  }, [])

  // Export to SVG
  const exportToSVG = useCallback(() => {
    const graphElement = document.querySelector('.react-flow')
    if (!graphElement) return

    toSvg(graphElement, {
      backgroundColor: '#0d1117',
      width: graphElement.offsetWidth,
      height: graphElement.offsetHeight,
    })
      .then((dataUrl) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
        downloadImage(dataUrl, `copybook-graph-${timestamp}.svg`)
      })
      .catch((err) => {
        console.error('Failed to export SVG:', err)
      })
  }, [])

  // Loading state
  if (loading) {
    return (
      <div className="graph-loading">
        <Loader2 size={32} className="spin" />
        <p>Loading copybook usage data...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="graph-error">
        <AlertCircle size={32} />
        <p>{error}</p>
      </div>
    )
  }

  // Empty state
  if (!copybookData || nodes.length === 0) {
    return (
      <div className="graph-empty">
        <FileText size={64} strokeWidth={1.5} />
        <h3>No Copybook Data Available</h3>
        <p>Run Code Analysis to generate copybook usage information.</p>
      </div>
    )
  }

  return (
    <div className="dependency-graph copybook-graph">
      {/* Toolbar */}
      <div className="graph-toolbar">
        <div className="toolbar-left">
          {/* Search */}
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search programs and copybooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Layout selector */}
          <div className="layout-selector">
            <Layers size={14} />
            <select
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
              disabled={isLayouting}
            >
              {LAYOUT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="toolbar-right">
          <button className="toolbar-btn" onClick={exportToPNG} title="Export as PNG">
            <Image size={16} />
          </button>
          <button className="toolbar-btn" onClick={exportToSVG} title="Export as SVG">
            <FileText size={16} />
          </button>
          <button className="toolbar-btn" onClick={refreshLayout} disabled={isLayouting}>
            <RotateCcw size={16} className={isLayouting ? 'spin' : ''} />
          </button>
          <button className="toolbar-btn" onClick={resetView}>
            <Maximize2 size={16} />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="graph-content">
        <div className="graph-canvas">
          <ReactFlow
            nodes={filteredNodes}
            edges={filteredEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
            }}
          >
            <Background color="#30363d" gap={20} />
            <Controls />

            {/* Legend */}
            <Panel position="top-left">
              <CopybookLegend
                nodeTypes={nodeTypeCounts}
                visibleTypes={visibleTypes}
                onToggleType={toggleTypeVisibility}
              />
            </Panel>

            {/* Info Panel */}
            <Panel position="top-right">
              <InfoPanel />
            </Panel>

            {/* Layout indicator */}
            {isLayouting && (
              <Panel position="top-center">
                <div className="layout-indicator">
                  <Loader2 size={14} className="spin" />
                  <span>Calculating layout...</span>
                </div>
              </Panel>
            )}

          </ReactFlow>
        </div>
      </div>
    </div>
  )
}

export default CopybookGraph
